import '../App.css';
import {
    Container, Card, Space, Table, Anchor, Text,
    createStyles, rem, Group, Divider, Accordion, Badge, Box
} from '@mantine/core';
import Footer from '../components/Footer';
import Head from '../components/Head';
import ReactMarkdown from 'react-markdown';
import { IconShieldLock, IconMail, IconList } from '@tabler/icons-react'

// ── Category colours ─────────────────────────────────────────────────────────
type Category = 'data' | 'rights' | 'legal'
const CAT_COLOR: Record<Category, string> = {
    data: '#2b9eb3',   // teal  — data collection
    rights: '#1a9e2e',   // green — user rights
    legal: '#532d86',   // purple — legal / compliance
}
const CAT_LABEL: Record<Category, string> = {
    data: 'Data Collection',
    rights: 'Your Rights',
    legal: 'Legal',
}

// ── Styles ────────────────────────────────────────────────────────────────────
const useStyles = createStyles((theme) => ({

    heroWrapper: {
        background: 'radial-gradient(ellipse at 50% 60%, #ede0ff 0%, #e8f6fb 50%, #f4fbff 100%)',
        paddingTop: rem(32),
        paddingBottom: rem(36),
        textAlign: 'center',
    },

    pageTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        letterSpacing: '-0.02em',
        fontSize: rem(52),
        color: '#5c0099',

        [theme.fn.smallerThan('md')]: { fontSize: rem(38) },
        [theme.fn.smallerThan('sm')]: { fontSize: rem(28) },
    },

    dividerLabel: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(12),
        letterSpacing: '0.08em',
        textTransform: 'uppercase' as const,
        color: theme.colors.gray[5],
    },

    sectionDivider: {
        margin: `${rem(28)} 0 ${rem(20)}`,
    },

    // ── TOC ───────────────────────────────────────────────────────────────────
    tocCard: {
        background: 'rgba(255,255,255,0.8)',
        borderLeft: `4px solid #7b00b8`,
    },

    tocLink: {
        display: 'block',
        padding: `${rem(4)} 0`,
        fontSize: rem(14),
        color: '#7b00b8',
        textDecoration: 'none',
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 500,
        transition: 'color 0.15s ease, padding-left 0.15s ease',

        '&:hover': {
            color: '#532d86',
            paddingLeft: rem(6),
        },
    },

    // ── Section cards ─────────────────────────────────────────────────────────
    sectionCard: {
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: theme.shadows.sm },
    },

    sectionTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(20),
    },

    // ── Prose ─────────────────────────────────────────────────────────────────
    prose: {
        fontSize: rem(15),
        lineHeight: 1.7,
        color: theme.colors.gray[8],
    },

    // ── Contact callout ───────────────────────────────────────────────────────
    contactCard: {
        background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
        border: `2px solid #2b9eb3`,
        borderRadius: theme.radius.lg,
        padding: `${rem(24)} ${rem(32)}`,
        textAlign: 'center',
    },
}))

// ── Privacy section data ──────────────────────────────────────────────────────
interface Section {
    id: string
    title: string | null
    category: Category
    content: string
    collapsible?: boolean
}

const privacySections: Section[] = [
    {
        id: 'intro', title: null, category: 'data',
        content: `
**Last Updated: August 12, 2026**

This privacy policy (**"Privacy Policy"**) describes how Pobblebonk, LLC (**"our", "us"** or **"we"**) collects, uses, shares, and store personal information of clients and users of our website comedyseen.com, web application, products and services (collectively, the **"Services"**) on or in which it is posted, linked, or referenced.

By using the Services, you accept the terms of this Privacy Policy and our Terms of Service, and consent to our collection, use, disclosure, and retention of your information as described in this Privacy Policy. If you have not done so already, please also review our Terms of Service. The Terms of Service contain provisions that limit our liability to you and require you to resolve any dispute with us on an individual basis and not as part of any class or representative action. IF YOU DO NOT AGREE WITH ANY PART OF THIS PRIVACY POLICY OR OUR TERMS OF SERVICE, THEN PLEASE DO NOT USE ANY OF THE SERVICES.

**Contests & Promotions:** Participation in any promotion, contest, or leaderboard requires an active user account. By entering any promotion through your account, you acknowledge that your account information and submission materials will be collected, used, and shared to administer your entry under this Privacy Policy and the designated official rules for that promotion.

Please note that this Privacy Policy does not apply to information collected through third-party websites or services that you may access through the Services or that you submit to us through email or other electronic message or offline.
`
    },
    {
        id: 'collection', title: 'What We Collect', category: 'data',
        content: `
**Information You Give Us.** Information we collect from you may include:
* **Personal information**, such as your name, city, state, or country;
* **Contact information**, such as your email address or social media handles;
* **Tax Information (Prize Winners Only):** We do not collect tax IDs from general users. However, if you win a prize or cumulative prizes valued at $2,000 or more within a calendar year, we are required by law to collect your Social Security Number (SSN) or Tax ID solely to issue an IRS Form 1099.
* **Profile information**, such as your username, password, preferences, headshots, bio, feedback and survey responses;
* **Transaction information**, such as details about purchases you make through the Services (tickets) and preferences;
* **Marketing information**, such as your preferences for receiving marketing communications and details about how you engage with them;
* **Other information** not specifically listed here, which we will use as described in this Privacy Policy or as otherwise disclosed at the time of collection.

**Mobile Device Permissions.**
* **Camera and Photos:** With your permission, we may access your camera or photo library to allow you to upload profile photos, event flyers, or to scan ticket QR codes. You may revoke this permission at any time in your device settings.

**Automatic Data Collection.** We may automatically record certain information about how you use our Services (we refer to this information as **"Log Data"**). Log Data may include information such as a user's Internet Protocol (IP) address, device and browser type, operating systems, the pages or features of our Services browsed and the time spent, the frequency of use, search terms, and other statistics.

**Cookies & Location.**
* We may use cookies, local storage or similar technologies to analyze trends, administer the Services, track users' movements, and gather demographic information.
* This Service utilizes information about the location of your device, including GPS location, for purposes of enhancing or facilitating the Services. If you do not want us to collect this information do not download the Service or delete it from your device. You may also opt out at any time by emailing info@getcomedyseen.com.
`
    },
    {
        id: 'usage', title: 'Use of Personal Information', category: 'data',
        content: `
We use the collected data for various purposes or as otherwise described at the time of collection:

**Service Delivery.** We use your personal information to:
* provide, operate and improve the Services and our business;
* allow for check-ins to venues and events;
* communicate with you about the Services, including by sending announcements, updates, promotional notifications, security alerts, and support messages;
* post competition scoring and submission information within the Services, including on global and local leaderboards and co-branded promotion pages.

**Research and Development.** We may use your personal information for research and development purposes, including to analyze and improve the Services and our business.

**Compliance and Protection.** We may use your personal information to comply with applicable laws, lawful requests, and legal processes, to protect rights and privacy, enforce the Terms of Service, and prevent fraudulent or illegal activity.

**With Your Consent.** We may use, share or collect your personal information with your consent, such as when required by law.
`
    },
    {
        id: 'disclosure', title: 'Disclosures of Your Data', category: 'data',
        content: `
We may share your data with the following parties and as otherwise described in this Privacy Policy or at the time of collection.

* **Affiliates:** We may disclose your personal information to our subsidiaries and corporate affiliates for purposes consistent with this Privacy Policy.
* **Promotional Partners & Sponsors:** When you participate in a contest, leaderboard, or promotion offered in partnership with a third party (such as *Pro Performance Wheels*), we may share your account details, entry information, and submission metadata with that partner to administer the promotion, verify eligibility, deliver prizes, and conduct joint marketing as permitted by law.
* **Authorities and Others:** Law enforcement, government authorities, and private parties, as we believe in good faith to be necessary or appropriate for compliance and protection purposes.
* **Business Transfers:** We may share personal information when we do a business deal, or negotiate a business deal, involving the sale or transfer of all or a part of our business or assets.
* **General Public & Promotion Features:** Please note that any information, check-in data, score submissions, or media you post or disclose through these Services or as part of a promotional contest will become public and may be available to other users, partner co-sponsors, and the general public.
* **Professional Advisors and Service Providers:** We may share information with those who need it to work for us, including lawyers, bankers, auditors, and insurers.
`
    },
    {
        id: 'retention', title: 'Data Retention', category: 'data',
        content: `
We retain information we collect as long as it is necessary and relevant to fulfill the purposes outlined in this Privacy Policy. In addition, we retain personal information to comply with applicable law where required, prevent fraud, resolve disputes, troubleshoot problems, assist with any investigation, enforce our Terms of Service, and other actions permitted by law.

In some circumstances we may anonymize your personal information so that it can no longer be associated with you, in which case we may use this information indefinitely without further notice to you.

We employ industry standard security measures designed to protect the security of all information submitted through the Services. However, the security of information transmitted through the Internet can never be guaranteed.
`
    },
    {
        id: 'rights', title: 'Your Rights', category: 'rights', collapsible: true,
        content: `
You have the following rights with respect to your personal information:

* You have the right to ask us for an overview of your personal information that we process;
* You have the right to ask us to transfer your personal information directly to you or to another entity;
* You may ask us to delete your personal information if we no longer need it for its original purpose, if you object to us processing it, or if we unlawfully process your personal information;
* You can object to us using your personal information for our own legitimate interests;
* You have the right to ask us to restrict using your personal information;
* If your personal information is incorrect, you have the right to ask us to rectify it;
* Should you not be satisfied with the way we have responded to your concerns, you have the right to submit a complaint to us or to the data protection authority in your country.
* You can set your browser to refuse all or some browser cookies, or to alert you when cookies are being sent.
* You can choose to disallow the Service to collect and use real-time information about your device's location by emailing a request to info@getcomedyseen.com.
* If you do not want us to use your contact information to promote our own or third parties' products or services, you can opt-out by emailing info@getcomedyseen.com.

### Do Not Track

"Do Not Track" is a privacy preference you can set in most web browsers. Please note that we currently do not respond to Do Not Track browser settings.
`
    },
    {
        id: 'payments', title: 'Third Party Payment Service', category: 'legal',
        content: `
The Services may provide functionality allowing you to make payments using third-party payment services. When you use such a service to make a payment to us, your personal information will be collected by such third party and not by us and will be subject to the third party's privacy policy, rather than this Privacy Policy. We have no control over, and are not responsible for, this third party's collection, use, and disclosure of your personal information.
`
    },
    {
        id: 'minors', title: 'Minors', category: 'legal',
        content: `
We do not intentionally gather personal information from visitors who are under the age of 18. Our Terms of Service require all users to be over the age of 18. If you are a parent or guardian agreeing to the terms for the benefit of a minor, then you agree to and accept full responsibility for that child's use of the websites and the Services, including all financial charges and legal liability that he or she may incur.
`
    },
    {
        id: 'international', title: 'International Data Transfers', category: 'legal',
        content: `
We have offices outside of the EU and your personal information may be transferred to, and processed in or from, the United States of America (the "US") or other locations outside of your state, province, country or other governmental jurisdiction where privacy laws may not be as protective as those in your jurisdiction.

EU users should read the important information provided below about the transfer of personal information outside of the European Economic Area.
`
    },
    {
        id: 'tos', title: 'Terms of Service', category: 'legal',
        content: `
If you choose to use the Service, your visit and any dispute over privacy is subject to this Policy and our Terms of Service http://www.comedyseen.com/tos, including limitations on damages, resolution of disputes, and application of the law of the state of Florida.
`
    },
    {
        id: 'changes', title: 'Changes to this Privacy Policy', category: 'legal',
        content: `
We may change this privacy at any time. We encourage you to periodically review this page for the latest information about our privacy practices. If we make any changes, we will change the Last Updated date above.

Any modifications to this Privacy Policy will be effective upon our posting of the new terms and/or upon the implementation of the changes to the Services. In all cases, your continued use of the Services after the posting of any modified Privacy Policy indicates your acceptance of the terms of the modified Privacy Policy.
`
    },
    {
        id: 'california', title: 'Notice to California Residents', category: 'legal', collapsible: true,
        content: `
Under California Civil Code Section 1789.3, California users are entitled to the following consumer rights notice: California residents may reach the Complaint Assistance Unit of the Division of Consumer Services of the California Department of Consumer Affairs by mail at 1625 North Market Blvd., Sacramento, CA 95834, or by telephone at (916) 445-1254 or (800) 952-5210.

This section provides additional details about the personal information we collect about California consumers and the rights afforded to them under the California Consumer Privacy Act or "CCPA".

For more details about the personal information we collect from you, please see the "What we Collect" section above. We collect this information for the business and commercial purposes described in the "Use of Personal Information" section above. We share this information with the categories of third parties described in the "Disclosures of Your Data" section above. We do not sell (as such term is defined in the CCPA) the personal information we collect (and will not sell it without providing a right to opt out).

Subject to certain limitations, the CCPA provides California consumers the right to request to know more details about the categories or specific pieces of personal information we collect (including how we use and disclose this information), to delete their personal information, to opt out of any "sales" that may be occurring, and to not be discriminated against for exercising these rights.

California consumers may make a request pursuant to their rights under the CCPA by contacting us at info@getcomedyseen.com. Please note that you must verify your identity and request before further action is taken. As a part of this process, government identification may be required. Consistent with California law, you may designate an authorized agent to make a request on your behalf.
`
    },
    {
        id: 'other-states', title: 'Other State Privacy Rights', category: 'legal',
        content: `
State consumer privacy laws may provide their residents with additional rights regarding our use of their personal information. To learn more about these rights please send an email to info@getcomedyseen.com.
`
    },
    {
        id: 'eu', title: 'Notice to European Users', category: 'legal', collapsible: true,
        content: `
The information provided in this notice applies only to individuals in the European Economic Area, Switzerland, and the United Kingdom (collectively, **"EU"**) and "personal information" as used in this Privacy Policy is equivalent to "personal data" as defined in the European Union General Data Protection Regulation (GDPR).

**Sensitive Data.** Some of the information you provide us may constitute sensitive data as defined in the GDPR (also referred to as special categories of personal data), including identification of your race or ethnicity on government-issued identification documents.

**Legal Basis for Processing.** We only use your personal information as permitted by law.
`
    },
]

// ── GDPR table data ───────────────────────────────────────────────────────────
const gdprData = [
    { purpose: 'To provide our Service', basis: 'Our processing of your personal information is necessary to perform the contract governing our provision of the Services or to take steps that you request prior to signing up for the Services.' },
    { purpose: 'To communicate with you; To optimize our Services; For compliance, fraud prevention, and safety; For research and development', basis: 'These processing activities constitute our legitimate interests. We make sure we consider and balance any potential impacts on you (both positive and negative) and your rights before we process your personal information for our legitimate interests.' },
    { purpose: 'To comply with law', basis: 'We use your personal information to comply with applicable laws and our legal obligations.' },
    { purpose: 'With your consent', basis: 'Where our use of your personal information is based upon your consent, you have the right to withdraw it anytime by contacting us at info@getcomedyseen.com.' },
]

const euEndContent = `
**Use for New Purposes.** We may use your personal information for reasons not described in this Privacy Policy where we are permitted by law to do so and where the reason is compatible with the purpose for which we collected it.

**Your Rights.** Under the GDPR, you may ask us to:
* **Opt-out.** Stop sending you direct marketing communications which you have previously consented to receive.
* **Access.** Provide you with information about our processing of your personal information and give you access to your personal information.
* **Correct.** Update or correct inaccuracies in your personal information.
* **Delete.** Delete your personal information.
* **Transfer.** Transfer a machine-readable copy of your personal information to you or a third party of your choice.
* **Restrict.** Restrict the processing of your personal information.
* **Object.** Object to our reliance on our legitimate interests as the basis of our processing of your personal information that impacts your rights.

You can submit these requests by email to info@getcomedyseen.com. We may request specific information from you to help us confirm your identity and process your request.

**Cross-Border Data Transfer.** We process personal data in the US and data protection laws may be different from those in your country of residence. You consent to the transfer of your information, including personal information, to the US as set forth in this Privacy Policy by using our Services.

**Representative for data subjects in the EU and UK.** We value your privacy and your rights as a data subject and have therefore appointed Prighter Group with its local partners as our privacy representative and your point of contact. If you want to contact us via our representative, Prighter, or make use of your data subject rights, please visit: [https://prighter.com/q/17669831030](https://prighter.com/q/17669831030)
`

// ── TOC sections (only titled ones) ──────────────────────────────────────────
const tocSections = privacySections.filter(s => s.title)

// ── Main component ─────────────────────────────────────────────────────────────
function Pp() {
    const { classes } = useStyles()

    const links = [
        { "link": "/About", "label": "About" },
        { "link": "/Support", "label": "Support" },
        { "link": "/Pp", "label": "Privacy" },
        { "link": "/Tos", "label": "Terms of Use" },
        { "link": "/Changelog", "label": "Changelog" },
    ]

    const renderProse = (content: string) => (
        <div className={classes.prose}>
            <ReactMarkdown>{content}</ReactMarkdown>
        </div>
    )

    return (
        <div className="privacy-wrapper">
            <Head />

            {/* ── Hero ── */}
            <div className={classes.heroWrapper}>
                <Group position="center" spacing="xs" mb={rem(8)}>
                    <IconShieldLock size={44} color="#5c0099" />
                </Group>
                <Text className={classes.pageTitle}>Privacy Policy</Text>
                <Text size="lg" color="dimmed" mt={6}>Your data &amp; your rights — clearly explained</Text>
            </div>

            <Container size="md" py="xl">

                {/* ── Table of Contents ── */}
                <Divider
                    className={classes.sectionDivider}
                    label={<span className={classes.dividerLabel}><IconList size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Contents</span>}
                    labelPosition="center"
                    size="xs"
                />

                <Card className={classes.tocCard} shadow="xs" padding="lg" radius="md" withBorder mb="xl">
                    <Box
                        sx={{
                            columns: '2 200px',
                            columnGap: '2rem',
                        }}
                    >
                        {tocSections.map(s => (
                            <a key={s.id} href={`#${s.id}`} className={classes.tocLink}>
                                {s.title}
                            </a>
                        ))}
                    </Box>
                </Card>

                {/* ── Sections ── */}
                {privacySections.map((section) => {
                    const borderColor = CAT_COLOR[section.category]

                    return (
                        <Card
                            key={section.id}
                            id={section.id}
                            className={classes.sectionCard}
                            shadow="sm"
                            padding="lg"
                            radius="md"
                            withBorder
                            mb="lg"
                            sx={{ borderLeft: `4px solid ${borderColor}` }}
                        >
                            {section.title && (
                                <Group mb="md" spacing="sm">
                                    <Text className={classes.sectionTitle} sx={{ color: borderColor }}>
                                        {section.title}
                                    </Text>
                                    <Badge
                                        color={section.category === 'data' ? 'cyan' : section.category === 'rights' ? 'green' : 'violet'}
                                        variant="light"
                                        size="xs"
                                        radius="sm"
                                        sx={{ fontFamily: "'Outfit', sans-serif", flexShrink: 0 }}
                                    >
                                        {CAT_LABEL[section.category]}
                                    </Badge>
                                </Group>
                            )}

                            {section.collapsible ? (
                                <Accordion variant="filled" radius="md">
                                    <Accordion.Item value="content">
                                        <Accordion.Control
                                            sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: rem(14), color: borderColor }}
                                        >
                                            Read full section
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                            {renderProse(section.content)}

                                            {/* GDPR table inside EU section */}
                                            {section.id === 'eu' && (
                                                <>
                                                    <Space h="md" />
                                                    <Table striped highlightOnHover withBorder>
                                                        <thead>
                                                            <tr>
                                                                <th>Processing Purpose</th>
                                                                <th>Legal Basis</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {gdprData.map((row) => (
                                                                <tr key={row.purpose}>
                                                                    <td style={{ verticalAlign: 'top', fontSize: rem(13) }}>{row.purpose}</td>
                                                                    <td style={{ verticalAlign: 'top', fontSize: rem(13) }}>{row.basis}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                    <Space h="md" />
                                                    {renderProse(euEndContent)}
                                                </>
                                            )}
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                </Accordion>
                            ) : (
                                <>
                                    {renderProse(section.content)}
                                    {section.id === 'eu' && (
                                        <>
                                            <Space h="md" />
                                            <Table striped highlightOnHover withBorder>
                                                <thead>
                                                    <tr>
                                                        <th>Processing Purpose</th>
                                                        <th>Legal Basis</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {gdprData.map((row) => (
                                                        <tr key={row.purpose}>
                                                            <td style={{ verticalAlign: 'top' }}>{row.purpose}</td>
                                                            <td style={{ verticalAlign: 'top' }}>{row.basis}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                            <Space h="md" />
                                            {renderProse(euEndContent)}
                                        </>
                                    )}
                                </>
                            )}
                        </Card>
                    )
                })}

                {/* ── Contact callout ── */}
                <Divider
                    className={classes.sectionDivider}
                    label={<span className={classes.dividerLabel}>Contact us</span>}
                    labelPosition="center"
                    size="xs"
                />

                <div className={classes.contactCard}>
                    <Group position="center" spacing="xs" mb="xs">
                        <IconMail size={22} color="#0d6a7a" />
                        <Text fw={700} sx={{ fontFamily: "'Outfit', sans-serif", color: '#0d6a7a' }}>
                            Questions about this policy?
                        </Text>
                    </Group>
                    <Anchor href="mailto:info@getcomedyseen.com" underline={false}>
                        <Text fw={700} sx={{ fontFamily: "'Outfit', sans-serif", fontSize: rem(18), color: '#0d6a7a' }}>
                            info@getcomedyseen.com
                        </Text>
                    </Anchor>
                    <Text size="xs" color="dimmed" mt={6}>We'll respond within 1–2 business days</Text>
                </div>

                <Space h="xl" />
            </Container>

            <Footer links={links} />
        </div>
    )
}

export default Pp;